import {
  getGameActive,
  getMeVisibilityGroupsTeams,
  getPlayersSearch,
  PlayerPublic,
  TeamPair,
} from "@aura-frontend/omnibus-az-client";
import { useParams } from "react-router-dom";

import {
  useFetchTeamJerseyNumbers,
  withLineupJerseyNumbers,
} from "@/hooks/useTeamJerseyNumbers";

const unifyPlayersByExternalId = (players: PlayerPublic[]) => {
  const unifiedPlayers = players.reduce(
    (acc, cur) => {
      acc[cur.external_ids[0]] = cur;
      return acc;
    },
    {} as Record<string, PlayerPublic>,
  );

  return Object.values(unifiedPlayers);
};

const createSearchPlayerRequest = (visibilityGroup: string, teamId: string) => {
  return getPlayersSearch({
    query: {
      visibilityGroup,
      teamId,
    },
  }).then((res) => res.data?.players ?? []);
};

const fetchPlayersForGame = async (teams: TeamPair) => {
  // Fetch visibility groups
  const visibilityGroups = await getMeVisibilityGroupsTeams();

  // Create requests to fetch players for away team for each visibility group
  const awayTeamRequests = visibilityGroups.data?.visibilityGroups.map(
    (visibilityGroup) =>
      createSearchPlayerRequest(visibilityGroup, teams.away as string),
  );

  // Create requests to fetch players for home team for each visibility group
  const homeTeamRequests = visibilityGroups.data?.visibilityGroups.map(
    (visibilityGroup) =>
      createSearchPlayerRequest(visibilityGroup, teams.home as string),
  );

  // Fetch players for away team for each visibility group
  const awayTeamPlayers = await Promise.all(
    awayTeamRequests as Promise<PlayerPublic[]>[],
  ).then((res) => res.flat());

  // Fetch players for home team for each visibility group
  const homeTeamPlayers = await Promise.all(
    homeTeamRequests as Promise<PlayerPublic[]>[],
  ).then((res) => res.flat());

  return {
    awayTeamPlayers,
    homeTeamPlayers,
  };
};

export const useGetActiveGame = () => {
  const { id: machineId } = useParams() as { id: string };
  const fetchTeamJerseyNumbers = useFetchTeamJerseyNumbers();

  const fetchActiveGame = async () => {
    try {
      const { data } = await getGameActive({
        query: {
          machineUuid: machineId,
        },
        throwOnError: true,
      });

      const { game, gameState } = data;

      let unifiedAwayTeamPlayers: PlayerPublic[] = [];
      let unifiedHomeTeamPlayers: PlayerPublic[] = [];

      if (data?.game?.type === "game") {
        const { teams } = data.game;
        const [{ awayTeamPlayers, homeTeamPlayers }, awayJerseys, homeJerseys] =
          await Promise.all([
            fetchPlayersForGame(teams),
            fetchTeamJerseyNumbers(teams.away as string),
            fetchTeamJerseyNumbers(teams.home as string),
          ]);

        // Roster jersey numbers, so players subbed in carry the same number
        // the event form and Scorekeeping V2 put in lineups
        unifiedAwayTeamPlayers = withLineupJerseyNumbers(
          unifyPlayersByExternalId(awayTeamPlayers),
          awayJerseys,
        );
        unifiedHomeTeamPlayers = withLineupJerseyNumbers(
          unifyPlayersByExternalId(homeTeamPlayers),
          homeJerseys,
        );
      }

      return {
        gameState,
        game,
        awayTeamPlayers: unifiedAwayTeamPlayers,
        homeTeamPlayers: unifiedHomeTeamPlayers,
      };
    } catch (e) {
      // This request return an error when there is no active game, so that's why we return null here.
      return null;
    }
  };

  return { fetchActiveGame };
};
